import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Combobox, DataTable, Dialog, EmptyState, Panel, Stack, t, Text, Textarea, toast } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { api, ApiFailure, causeOf, unwrap } from '../../lib/api';
import { relativeTime } from '../../lib/format';
import { pendingLabel } from '../PendingButton';
import { changeRef, PullRequestNotice } from './PullRequestNotice';

type Team = maintenance.operations['getProjectTeam']['responses']['200']['content']['application/json'];
type Member = Team['team']['developers'][number];
type Row = Member & { role: 'lead' | 'developer' };

const emailsOf = (text: string) => [...new Set(text.split(/[\s,;]+/).map(e => e.trim().toLowerCase()).filter(Boolean))];

/**
 * SPEC-034 § 12.10 (lot 1.5, slice 3): « Équipe du projet ». Entra says who
 * may be a developer or a lead (two global groups); the team says on which
 * project. Rights need both: an entry out of its group is shown orphan and
 * gives nothing. The leads are the GitHub team lead-<project> the platform keeps.
 */
export function ProjectTeam({ project, editing, onEditClose }: {
  project: { name: string; title: string };
  /** « Modifier l'équipe » is the tab's action, in the page's tab bar (DSM Tabs). */
  editing: boolean; onEditClose: () => void;
}) {
  const queryClient = useQueryClient();
  const team = useQuery({
    queryKey: ['project-team', project.name],
    queryFn: async () => unwrap(await api.maintenance.GET('/projects/{project}/team', { params: { path: { project: project.name } } })),
    refetchInterval: 30_000,
  });
  const [leads, setLeads] = useState<string[]>([]);
  const [developers, setDevelopers] = useState<string[]>([]);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [sending, setSending] = useState(false);

  const data = team.data;
  const open = () => {
    setLeads(data?.team.leads.map(m => m.email) ?? []);
    setDevelopers(data?.team.developers.map(m => m.email) ?? []);
    setFailure(null);
  };
  // Opened from the tab bar: the form starts from the team as it is.
  useEffect(() => { if (editing) open(); }, [editing]);
  const save = async () => {
    setSending(true);
    setFailure(null);
    try {
      const { pullRequest } = unwrap(await api.maintenance.PUT('/projects/{project}/team', { params: { path: { project: project.name } }, body: { developers, leads } }));
      toast({ tone: 'pos', title: t('team.saved', { ref: changeRef(pullRequest) }) });
      onEditClose();
      await queryClient.invalidateQueries({ queryKey: ['project-team', project.name] });
    } catch (e) {
      setFailure(e instanceof ApiFailure ? e : null);
    } finally {
      setSending(false);
    }
  };

  const rows: Row[] = [...(data?.team.leads ?? []).map(m => ({ ...m, role: 'lead' as const })), ...(data?.team.developers ?? []).map(m => ({ ...m, role: 'developer' as const }))];
  const github = data?.github ?? null;
  const options = (list: { name: string; email: string }[] | undefined, chosen: string[]) =>
    [...(list ?? []).map(c => ({ value: c.email, label: `${c.name} · ${c.email}` })), ...chosen.filter(e => !(list ?? []).some(c => c.email === e)).map(e => ({ value: e, label: e }))];

  return (
    <Stack gap={16}>
      {data?.pullRequest && <PullRequestNotice pullRequest={data.pullRequest} />}
      {github && github.state !== 'in_sync' && (
        <Alert tone="warn" title={t(`team.github.${github.state}Title`, { team: github.team })}>
          {github.state === 'unmapped' ? t('team.github.unmappedBody', { emails: github.unmapped.join(', ') }) : github.failure}
        </Alert>
      )}
      {/* Same frame as the other project tabs (Serveurs): Panel title + subtitle, flush around its table. */}
      <Panel
        title={t('team.title')}
        subtitle={t('team.subtitle')}
        flush
        footer={(
          <Text variant="caption" tone="mute">
            {[t('team.leadsNote', { team: data?.githubTeam ?? '' }), github ? t('team.github.checked', { team: github.team, when: relativeTime(github.checkedAt) }) : null].filter(Boolean).join(' ')}
          </Text>
        )}
      >
        <DataTable
          bordered={false}
          label={t('team.table')}
          rows={rows}
          rowKey={(r: Row) => `${r.role}-${r.email}`}
          loading={team.isPending}
          toolbar={false}
          rowHeight="auto"
          // « Modifier l'équipe » is the tab bar's action: not repeated here.
          empty={<EmptyState framed={false} icon="group" title={t('team.empty')} />}
          columns={[
            {
              key: 'email', label: t('team.person'), grow: true,
              render: (r: Row) => (
                <Stack gap={4}>
                  <Text variant="body-sm">{r.name ?? r.email}</Text>
                  {r.name && <Text variant="caption" tone="mute">{r.email}</Text>}
                </Stack>
              ),
            },
            { key: 'role', label: t('team.role'), render: (r: Row) => <Badge tone={r.role === 'lead' ? 'brand' : 'neutral'} size="sm">{t(`team.roles.${r.role}`)}</Badge> },
            {
              key: 'orphan', label: t('team.rights'),
              render: (r: Row) => (r.orphan === null ? <Text variant="body-sm" tone="mute">{t('team.unknown')}</Text>
                : r.orphan ? <Badge tone="warn" size="sm">{t('team.orphan')}</Badge> : <Text variant="body-sm">{t('team.active')}</Text>),
            },
          ]}
        />
      </Panel>

      <Dialog
        open={editing}
        onClose={onEditClose}
        size="md"
        title={t('team.dialogTitle')}
        description={t('team.dialogBody', { project: project.title })}
        confirmLabel={pendingLabel(t('team.save'), sending)}
        confirmDisabled={sending}
        onConfirm={() => void save()}
      >
        <Stack gap={16}>
          {failure && <Alert tone="neg" title={failure.body.message}>{failure.body.hint}{causeOf(failure) ? ` ${t('errors.cause', { cause: causeOf(failure)! })}` : ''}</Alert>}
          {data?.candidates ? (
            <>
              <Combobox id="team-leads" label={t('team.roles.leads')} multiple options={options(data.candidates.leads, leads)} value={leads} onChange={(v: string[]) => setLeads(v)} hint={t('team.leadsHint')} />
              <Combobox id="team-developers" label={t('team.roles.developers')} multiple options={options([...data.candidates.developers, ...data.candidates.leads], developers)} value={developers} onChange={(v: string[]) => setDevelopers(v)} hint={t('team.developersHint')} />
            </>
          ) : (
            <>
              <Alert tone="info" title={t('team.noGraphTitle')}>{t('team.noGraphBody')}</Alert>
              <Textarea id="team-leads-text" label={t('team.roles.leads')} value={leads.join('\n')} onChange={(v: string) => setLeads(emailsOf(v))} hint={t('team.leadsHint')} rows={3} />
              <Textarea id="team-developers-text" label={t('team.roles.developers')} value={developers.join('\n')} onChange={(v: string) => setDevelopers(emailsOf(v))} hint={t('team.developersHint')} rows={4} />
            </>
          )}
        </Stack>
      </Dialog>
    </Stack>
  );
}
